import Marquee from "react-fast-marquee";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import {
  Sun, Zap, Ruler, MapPin, Leaf, Users, Sprout, Fish, Building2, Snowflake,
  Coins, ShieldCheck, TrendingUp, Layers, ArrowRight, CheckCircle2, Wrench, Cpu, Repeat,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Chapter, SectionTitle } from "@/components/Reveal";

const IMG = {
  agri: "https://images.pexels.com/photos/11733080/pexels-photo-11733080.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  community: "https://images.unsplash.com/photo-1667844551497-686b8847986f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxhZnJpY2FuJTIwaW5mcmFzdHJ1Y3R1cmUlMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3ODcyODUyODB8MA&ixlib=rb-4.1.0&q=85",
  investors: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBpbnZlc3RvciUyMG1lZXRpbmclMjBoaWdoJTIwZW5kfGVufDB8fHx8MTc4NzI4NTI5NHww&ixlib=rb-4.1.0&q=85",
};

const wrap = "max-w-7xl mx-auto px-5 sm:px-8";

/* ---------------- ABOUT ---------------- */
function About() {
  const { t } = useLang();
  const steps = [
    { i: Wrench, t: t("about.s1_t"), d: t("about.s1") },
    { i: Cpu, t: t("about.s2_t"), d: t("about.s2") },
    { i: Repeat, t: t("about.s3_t"), d: t("about.s3") },
  ];
  return (
    <section id="about" className="py-24 sm:py-32 noise-bg">
      <div className={wrap}>
        <Chapter>{t("about.chapter")}</Chapter>
        <SectionTitle>{t("about.title")}</SectionTitle>
        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          <Reveal className="bg-white rounded-2xl border border-slate-200 p-8 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
            <div className="mono text-xs tracking-widest text-secondary uppercase mb-3">{t("about.vision_t")}</div>
            <p className="text-slate-700 text-lg leading-relaxed">{t("about.vision")}</p>
          </Reveal>
          <Reveal delay={0.1} className="bg-white rounded-2xl border border-slate-200 p-8 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
            <div className="mono text-xs tracking-widest text-secondary uppercase mb-3">{t("about.mission_t")}</div>
            <p className="text-slate-700 text-lg leading-relaxed">{t("about.mission")}</p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="mt-8 bg-sky-900 rounded-2xl p-8 sm:p-12 text-white overflow-hidden relative">
          <h3 className="font-display text-2xl font-light mb-3">{t("about.boot_t")}</h3>
          <p className="text-sky-100 max-w-3xl leading-relaxed">{t("about.boot")}</p>
          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="flex gap-4" data-testid={`boot-step-${i}`}>
                <div className="shrink-0 w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                  <s.i className="w-5 h-5 text-sky-300" />
                </div>
                <div>
                  <div className="mono text-xs text-sky-300 mb-1">0{i + 1}</div>
                  <div className="font-medium">{s.t}</div>
                  <div className="text-sm text-sky-200/80 mt-1">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- TECHNICAL ---------------- */
function Technical() {
  const { t } = useLang();
  const specs = [
    { i: Zap, l: t("technical.dc"), v: "300 MWc" },
    { i: Zap, l: t("technical.ac"), v: "250 MWac" },
    { i: Sun, l: t("technical.prod"), v: "533,75 GWh/an" },
    { i: Layers, l: t("technical.tech"), v: t("technical.tech_v") },
    { i: Ruler, l: t("technical.surface"), v: "≈ 180 ha" },
    { i: Ruler, l: t("technical.height"), v: t("technical.height_v") },
  ];
  return (
    <section id="technical" className="py-24 sm:py-32 bg-white">
      <div className={wrap}>
        <Chapter>{t("technical.chapter")}</Chapter>
        <SectionTitle>{t("technical.title")}</SectionTitle>
        <div className="mt-14 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
            {specs.map((s, i) => (
              <Reveal key={i} delay={i * 0.05} className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-sky-300 transition-colors duration-200" >
                <s.i className="w-5 h-5 text-secondary mb-4" />
                <div className="text-sm text-slate-500">{s.l}</div>
                <div className="mono text-xl text-slate-900 mt-1">{s.v}</div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden border border-slate-200 h-full bg-sky-950 relative min-h-[320px]">
              <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 60% 40%, #0ea5e9 0%, transparent 55%)" }} />
              <div className="relative p-8 h-full flex flex-col justify-between">
                <div className="flex items-center gap-2 text-sky-300">
                  <MapPin className="w-5 h-5" />
                  <span className="mono text-xs tracking-widest uppercase">{t("technical.loc")}</span>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className="relative">
                    <span className="absolute -inset-6 rounded-full bg-sky-500/20 animate-ping" style={{ animationDuration: "2.5s" }} />
                    <span className="relative flex w-5 h-5 rounded-full bg-sky-400 ring-4 ring-sky-400/30" />
                  </div>
                </div>
                <div>
                  <div className="font-display text-2xl text-white font-light">{t("technical.map_title")}</div>
                  <div className="text-sky-200 text-sm mt-1">{t("technical.map_desc")}</div>
                  <div className="mono text-xs text-sky-400 mt-3">14.15° N, 15.75° W</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-6 flex items-start gap-3 text-slate-600 max-w-3xl">
          <Sun className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
          <p className="leading-relaxed">{t("technical.feature")}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- FINANCE ---------------- */
function Finance() {
  const { t } = useLang();
  const kpis = [
    { i: Coins, l: t("finance.capex"), v: "≈ 182 M€" },
    { i: TrendingUp, l: t("finance.tariff"), v: "20,80 FCFA/kWh" },
    { i: ShieldCheck, l: t("finance.ppa"), v: "25 ans" },
    { i: Zap, l: t("finance.lcoe"), v: "Compétitif" },
  ];
  const struct = [
    { name: t("finance.debt"), value: 70, color: "#0369A1" },
    { name: t("finance.equity"), value: 25, color: "#15803D" },
    { name: t("finance.sponsor"), value: 5, color: "#94A3B8" },
  ];
  const revenue = [
    { y: "2027", r: 42 }, { y: "2030", r: 45 }, { y: "2035", r: 48 },
    { y: "2040", r: 50 }, { y: "2050", r: 52 }, { y: "2066", r: 55 },
  ];
  const guarantees = [t("finance.g1"), t("finance.g2"), t("finance.g3"), t("finance.g4")];
  return (
    <section id="finance" className="py-24 sm:py-32 bg-slate-50 noise-bg">
      <div className={wrap}>
        <Chapter>{t("finance.chapter")}</Chapter>
        <SectionTitle>{t("finance.title")}</SectionTitle>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k, i) => (
            <Reveal key={i} delay={i * 0.05} className="bg-white rounded-xl border border-slate-200 p-6">
              <k.i className="w-5 h-5 text-sky-700 mb-4" />
              <div className="text-sm text-slate-500">{k.l}</div>
              <div className="mono text-lg text-slate-900 mt-1">{k.v}</div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 grid lg:grid-cols-2 gap-8">
          <Reveal className="bg-white rounded-2xl border border-slate-200 p-8">
            <h3 className="font-display text-xl font-medium text-slate-900 mb-6">{t("finance.struct_t")}</h3>
            <div className="grid grid-cols-2 gap-6 items-center">
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={struct} dataKey="value" innerRadius={52} outerRadius={80} paddingAngle={3} stroke="none">
                      {struct.map((s, i) => <Cell key={i} fill={s.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${v}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {struct.map((s) => (
                  <div key={s.name} className="flex items-center gap-3">
                    <span className="w-3 h-3 rounded-sm" style={{ background: s.color }} />
                    <span className="text-sm text-slate-700 flex-1">{s.name}</span>
                    <span className="mono text-sm text-slate-900">{s.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="bg-white rounded-2xl border border-slate-200 p-8">
            <h3 className="font-display text-xl font-medium text-slate-900 mb-6">{t("finance.rev_t")}</h3>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenue}>
                  <XAxis dataKey="y" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94a3b8" />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="r" fill="#15803D" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-8 bg-sky-900 rounded-2xl p-8 sm:p-10 text-white">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="w-5 h-5 text-sky-300" />
            <h3 className="font-display text-xl font-light">{t("finance.guarantees_t")}</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {guarantees.map((g, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`guarantee-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
                <span className="text-sky-100">{g}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- ESG ---------------- */
function ESG() {
  const { t } = useLang();
  return (
    <section id="esg" className="py-24 sm:py-32 bg-white">
      <div className={wrap}>
        <Chapter>{t("esg.chapter")}</Chapter>
        <SectionTitle>{t("esg.title")}</SectionTitle>
        <div className="mt-14 grid md:grid-cols-6 gap-4 auto-rows-[minmax(180px,auto)]">
          <Reveal className="md:col-span-3 md:row-span-2 bg-secondary text-white rounded-2xl p-8 flex flex-col justify-between overflow-hidden relative">
            <Leaf className="w-7 h-7 text-green-200" />
            <div>
              <div className="mono text-xs tracking-widest uppercase text-green-200">{t("esg.env_t")}</div>
              <div className="font-display text-6xl lg:text-7xl font-light mt-3">{t("esg.env_v")}</div>
              <div className="text-green-100 text-lg">{t("esg.env_u")}</div>
              <p className="text-green-100/80 mt-4 max-w-sm">{t("esg.env_d")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.05} className="md:col-span-3 bg-sky-900 text-white rounded-2xl p-8 flex flex-col justify-between">
            <Users className="w-6 h-6 text-sky-300" />
            <div>
              <div className="mono text-xs tracking-widest uppercase text-sky-300">{t("esg.social_t")}</div>
              <div className="font-display text-4xl font-light mt-2">{t("esg.social_v")}</div>
              <div className="text-sky-200">{t("esg.social_u")}</div>
              <p className="text-sky-100/70 text-sm mt-2">{t("esg.social_d")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-2xl p-6 flex flex-col justify-between">
            <Sprout className="w-6 h-6 text-secondary" />
            <div>
              <div className="font-medium text-slate-900">{t("esg.women_t")}</div>
              <p className="text-sm text-slate-600 mt-1">{t("esg.women_d")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-1 bg-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between">
            <Fish className="w-6 h-6 text-sky-400" />
            <div className="font-medium">{t("esg.econ_v")}</div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- AGRIVOLTAIC ---------------- */
function Agri() {
  const { t } = useLang();
  const badges = [t("agri.b1"), t("agri.b2"), t("agri.b3"), t("agri.b4")];
  return (
    <section id="agri" className="py-24 sm:py-32 bg-slate-950 text-white overflow-hidden">
      <div className={wrap}>
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Chapter>{t("agri.chapter")}</Chapter>
            <SectionTitle dark>{t("agri.title")}</SectionTitle>
            <Reveal delay={0.1}><p className="mt-6 text-slate-300 text-lg leading-relaxed">{t("agri.desc")}</p></Reveal>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <Reveal className="bg-white/5 border border-white/10 rounded-xl p-6">
                <Sprout className="w-6 h-6 text-green-400 mb-3" />
                <div className="font-medium">{t("agri.u1_t")}</div>
                <p className="text-sm text-slate-400 mt-1">{t("agri.u1")}</p>
              </Reveal>
              <Reveal delay={0.1} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <Fish className="w-6 h-6 text-sky-400 mb-3" />
                <div className="font-medium">{t("agri.u2_t")}</div>
                <p className="text-sm text-slate-400 mt-1">{t("agri.u2")}</p>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="mt-6 flex flex-wrap gap-2">
              {badges.map((b) => (
                <span key={b} className="mono text-xs px-3 py-1.5 rounded-full border border-white/15 text-slate-300">{b}</span>
              ))}
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={IMG.agri} alt="Agrivoltaïque" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- INFRASTRUCTURE ---------------- */
function Infra() {
  const { t } = useLang();
  const items = [
    { i: Building2, t: t("infra.i1_t"), d: t("infra.i1") },
    { i: Snowflake, t: t("infra.i2_t"), d: t("infra.i2") },
    { i: Coins, t: t("infra.i3_t"), d: t("infra.i3") },
  ];
  return (
    <section id="infra" className="py-24 sm:py-32 bg-white">
      <div className={wrap}>
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <Chapter>{t("infra.chapter")}</Chapter>
            <SectionTitle>{t("infra.title")}</SectionTitle>
            <Reveal delay={0.1} className="mt-8 rounded-2xl overflow-hidden border border-slate-200">
              <img src={IMG.community} alt="Communauté" className="w-full h-56 object-cover" />
            </Reveal>
          </div>
          <div className="lg:col-span-2 grid gap-4">
            {items.map((it, i) => (
              <Reveal key={i} delay={i * 0.08} className="group bg-slate-50 border border-slate-200 rounded-2xl p-8 flex gap-6 hover:bg-sky-900 hover:border-sky-900 transition-colors duration-300">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center group-hover:bg-white/10 group-hover:border-white/20 transition-colors duration-300">
                  <it.i className="w-5 h-5 text-sky-700 group-hover:text-sky-300 transition-colors duration-300" />
                </div>
                <div>
                  <div className="font-display text-xl text-slate-900 group-hover:text-white transition-colors duration-300">{it.t}</div>
                  <p className="text-slate-600 group-hover:text-sky-100 mt-2 transition-colors duration-300">{it.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- PARTNERS ---------------- */
function Partners() {
  const { t } = useLang();
  const partners = [
    { n: "Horizon Affaires", r: t("partners.sponsor") },
    { n: "Senelec", r: t("partners.offtaker") },
    { n: "I2T Switzerland", r: t("partners.technical") },
    { n: "BNDE", r: t("partners.financial") },
    { n: "BSIC", r: t("partners.financial") },
    { n: "État du Sénégal", r: t("partners.sponsor") },
  ];
  return (
    <section id="partners" className="py-24 sm:py-32 bg-slate-50 noise-bg">
      <div className={wrap}>
        <Chapter>{t("partners.chapter")}</Chapter>
        <SectionTitle>{t("partners.title")}</SectionTitle>
      </div>
      <Reveal delay={0.1} className="mt-14 marquee-fade">
        <Marquee speed={40} gradient={false} pauseOnHover>
          {partners.concat(partners).map((p, i) => (
            <div key={i} className="mx-16 flex flex-col items-center" data-testid="partner-logo">
              <span className="font-display text-2xl sm:text-3xl font-light text-slate-800 whitespace-nowrap">{p.n}</span>
              <span className="mono text-[10px] tracking-widest uppercase text-secondary mt-1">{p.r}</span>
            </div>
          ))}
        </Marquee>
      </Reveal>
    </section>
  );
}

/* ---------------- INVEST ---------------- */
function Invest() {
  const { t } = useLang();
  const reasons = [
    { i: TrendingUp, t: t("invest.r1_t"), d: t("invest.r1") },
    { i: ShieldCheck, t: t("invest.r2_t"), d: t("invest.r2") },
    { i: Leaf, t: t("invest.r3_t"), d: t("invest.r3") },
  ];
  return (
    <section id="invest" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMG.investors} alt="Investisseurs" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>
      <div className={`${wrap} relative`}>
        <Chapter>{t("invest.chapter")}</Chapter>
        <SectionTitle dark>{t("invest.title")}</SectionTitle>
        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {reasons.map((r, i) => (
            <Reveal key={i} delay={i * 0.08} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <r.i className="w-6 h-6 text-green-400 mb-5" />
              <div className="font-display text-xl text-white">{r.t}</div>
              <p className="text-slate-300 mt-2">{r.d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15} className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link
            to="/contact"
            data-testid="invest-cta-deck"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-secondary text-white px-7 py-3.5 font-medium hover:bg-green-700 active:scale-95 transition-[background-color,transform] duration-200"
          >
            {t("invest.cta1")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
          <Link
            to="/contact"
            data-testid="invest-cta-contact"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white px-7 py-3.5 font-medium hover:bg-white/10 active:scale-95 transition-[background-color,transform] duration-200"
          >
            {t("invest.cta2")}
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- TIMELINE ---------------- */
function Timeline() {
  const { t } = useLang();
  const phases = [
    { y: "2026", t: t("timeline.t1_t"), d: t("timeline.t1") },
    { y: "2026 – 2027", t: t("timeline.t2_t"), d: t("timeline.t2") },
    { y: "2027 – 2066", t: t("timeline.t3_t"), d: t("timeline.t3") },
    { y: "2066", t: t("timeline.t4_t"), d: t("timeline.t4") },
  ];
  return (
    <section id="timeline" className="py-24 sm:py-32 bg-white">
      <div className={wrap}>
        <Chapter>{t("timeline.chapter")}</Chapter>
        <SectionTitle>{t("timeline.title")}</SectionTitle>
        <div className="mt-16 relative">
          <div className="absolute left-0 top-6 right-0 h-px bg-slate-200 hidden md:block" />
          <div className="grid md:grid-cols-4 gap-8">
            {phases.map((p, i) => (
              <Reveal key={i} delay={i * 0.1} className="relative">
                <div className="w-3 h-3 rounded-full bg-secondary ring-4 ring-green-100 mb-6" />
                <div className="mono text-xs text-sky-700 tracking-widest">{p.y}</div>
                <div className="font-display text-xl text-slate-900 mt-1">{p.t}</div>
                <p className="text-slate-600 text-sm mt-2">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export { About, Technical, Finance, ESG, Agri, Infra, Partners, Invest, Timeline };
